// src/services/api.js
import axios from 'axios'
import config from '@/config'
import { getMockCompetitionData } from './mockData'

// Get API configuration from central config
const API_URL = config.api.baseUrl

// Flag to use mock data instead of real API
const USE_MOCK_DATA = false; // Set to false to use real API

/**
 * Fetch competition data from Disc Golf Metrix API
 */
export const fetchCompetitionData = async (id = config.api.competitionId) => {
    try {
        // If using mock data, return that instead of making a real API call
        if (USE_MOCK_DATA) {
            console.log('Using mock competition data instead of API');
            return getMockCompetitionData(id);
        }

        // Otherwise, make a real API call
        // Add timestamp to prevent caching
        const cacheBuster = Date.now()
        const response = await axios.get(`${API_URL}?content=result&id=${id}&cacheBuster=${cacheBuster}`)

        if (response.status !== 200) {
            throw new Error(`API request failed with status ${response.status}`)
        }

        // Process the data
        const data = response.data;

        // If this competition has subcompetitions, fetch them as well
        if (data.Competition && data.Competition.HasSubcompetitions === 1) {
            // If SubCompetitions don't exist or we want to force refresh them
            if (!data.Competition.SubCompetitions) {
                data.Competition.SubCompetitions = [];

                // If there are subcompetitions, load them
                if (data.Competition.ShowTourView === 0) {
                    // Wait for all subcompetitions to be fetched
                    await loadSubCompetitions(data.Competition);
                }
            }
        }

        return data;
    } catch (error) {
        console.error('Error fetching competition data:', error)
        throw error
    }
}

/**
 * Load subcompetitions for a main competition
 * This modifies the competition object by adding SubCompetitions array
 */
async function loadSubCompetitions(competition) {
    // If we already have subcompetition data, no need to fetch
    if (competition.SubCompetitions && competition.SubCompetitions.length > 0) {
        return;
    }

    try {
        competition.SubCompetitions = [];

        // If we have IDs of subcompetitions
        if (Array.isArray(competition.SubCompetitions)) {
            const promises = competition.SubCompetitions.map(async (subComp) => {
                if (subComp.ID) {
                    // Fetch the subcompetition data
                    const subData = await fetchCompetitionData(subComp.ID);
                    if (subData.Competition) {
                        return subData.Competition;
                    }
                }
                return null;
            });

            // Wait for all promises to resolve
            const subCompetitions = await Promise.all(promises);
            competition.SubCompetitions = subCompetitions.filter(comp => comp !== null);
        }
    } catch (error) {
        console.error('Error loading subcompetitions:', error);
    }
}

/**
 * Format course name (replace HTML entities)
 */
export const formatCourseName = (name) => {
    if (!name) return ''
    return name.replace(/&rarr;/g, '→').replace(/&amp;/g, '&')
}

/**
 * Format date to locale format
 */
export const formatDate = (dateStr) => {
    if (!dateStr) return ''

    // Assuming date format is YYYY-MM-DD
    const [year, month, day] = dateStr.split('-')
    const date = new Date(year, month - 1, day)

    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    })
}

/**
 * Format time to 24-hour format
 */
export const formatTime24Hour = (timeStr) => {
    if (!timeStr) return ''

    // Parse the time string (assuming format like "11:00:00")
    const [hours, minutes] = timeStr.split(':')

    // Return in 24-hour format
    return `${hours}:${minutes}`
}

export const formatUpdateTime = (date) => {
    if (!date) return '-';

    return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });
};

/**
 * Format competition name or course name (replace HTML entities with icons)
 */
export const formatCompetitionName = (name) => {
    if (!name) return '';

    // We'll keep this as a text replacement for use in places where we need plain text
    return name.replace(/&rarr;/g, '→').replace(/&amp;/g, '&');
};

/**
 * Format difference to par
 */
export const formatDiff = (diff) => {
    if (diff === 0) return 'E'
    return diff > 0 ? `+${diff}` : diff.toString()
}

/**
 * Get CSS class for score based on relation to par
 */
export const getScoreClass = (result, par) => {
    if (!result || !par) return 'score-par'

    const diff = parseInt(result.Diff)

    if (diff <= -2) return 'score-eagle'
    if (diff === -1) return 'score-birdie'
    if (diff === 0) return 'score-par'
    if (diff === 1) return 'score-bogey'
    if (diff >= 2) return 'score-double-bogey'

    return 'score-par'
}
